import React, { useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// Constants
import { COUNTRIES_LIST } from 'common/constants/countries'
import { BOOLEAN_OPTIONS, NATIONAL_ID_TYPE_ENTITY_OPTIONS } from 'common/constants/formOptions'

// Store
import { actions } from 'core/store'

// Hooks
import { useLocalStorage } from 'core/hooks/storage'
import { useCreateAccountEntity, useUpdateAccountEntity } from 'core/hooks/api'

// Styled Elements
import {
  FormWrapper,
  FormContainer,
  FormInputGroupItem,
  FormHeader,
  FormTitle,
  FormError,
  FormFooter,
  TableButtonWrapper,
  TableRowWrapper,
  TablePanelWrapper,
} from '../DirectoryForms.elements'

// Views
import {
  Form,
  FormSelect,
  FormTextField,
  FormTextArea,
  FormSearchSelect,
  Button,
  AccordionTableItem,
} from 'views/components'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

function EntityAccountForm(props) {
  // Destructure
  const { form, actions } = props

  // Hooks
  const [userCredentials] = useLocalStorage('userCredentials')
  const {
    createAccountEntity,
    accountEntityCreateData,
    accountEntityCreateError,
    isAccountEntityCreateLoading,
    isAccountEntityCreateSuccess,
  } = useCreateAccountEntity()
  const {
    updateAccountEntity,
    accountEntityUpdateData,
    accountEntityUpdateError,
    isAccountEntityUpdateLoading,
    isAccountEntityUpdateSuccess,
  } = useUpdateAccountEntity()

  // Store State
  const { toBeUpdatedAccount } = form

  // Store Actions
  const {
    setIsEntityAccountDrawerOpen,
    setToBeUpdatedAccount,
    showAlert,
    setShowHeaderLoader,
    setCreatedAccount,
    setIsPageTableUpdated,
    setIsRecordUpdated,
  } = actions

  // Internal State
  const [isCreateAccount, setIsCreateAccount] = useState(true)
  const [initialData, setInitialData] = useState()
  const [isFreeFormExpanded, setIsFreeFormExpanded] = useState(true)
  const [isStructuredExpanded, setIsStructuredExpanded] = useState(false)
  const [isNationalIDExpanded, setIsNationalIDExpanded] = useState(false)

  // Variables
  const formRef = useRef()
  const initialValues = {
    reference_id: '',
    customer_id: '',
    is_customer: undefined,
    email: '',
    name_legal: '',
    name_business: '',
    registration_country_code: '',
    address_line: '',
    address_street_no: '',
    address_street_name: '',
    address_building_name: '',
    address_floor: '',
    address_room: '',
    address_department: '',
    address_postbox: '',
    address_city_location: '',
    address_city: '',
    address_region: '',
    address_district: '',
    address_postcode: '',
    address_country_code: '',
    national_id_type_code: '',
    national_id: '',
    national_id_issue_country: '',
    national_id_registration_authority: '',
  }

  const validationSchema = Yup.object().shape({
    reference_id: Yup.string().max(50).required('Reference ID is required'),
    customer_id: Yup.string()
      .max(50)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      ),
    email: Yup.string().email().nullable(),
    name_legal: Yup.string().max(100).required('Legal Name is required'),
    name_business: Yup.string().max(100).nullable(),
    is_customer: Yup.bool().required('Specify if account is a customer'),
    registration_country_code: Yup.string().max(2).nullable(),
    address_line: Yup.string()
      .max(256)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test(
        'address_line',
        'Required if street number, street name, and building name are not specified',
        (value, context) => hasRequiredAddressFields(context),
      ),
    address_street_no: Yup.string()
      .max(10)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test('address_street_no', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context),
      ),
    address_street_name: Yup.string()
      .max(100)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test('address_street_name', 'Required with building name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context),
      ),
    address_building_name: Yup.string()
      .max(20)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test('address_building_name', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context),
      ),
    address_floor: Yup.string().max(20).nullable(),
    address_room: Yup.string().max(20).nullable(),
    address_department: Yup.string().max(20).nullable(),
    address_postbox: Yup.string().max(20).nullable(),
    address_city_location: Yup.string().max(50).nullable(),
    address_city: Yup.string()
      .max(50)
      .nullable()
      .test('address_city', 'Required if address fields are defined', (value, context) => hasAddress(value, context)),
    address_region: Yup.string().max(50).nullable(),
    address_district: Yup.string().max(50).nullable(),
    address_postcode: Yup.string().max(25).nullable(),
    address_country_code: Yup.string()
      .max(2)
      .nullable()
      .test('address_country_code', 'Required if address fields are defined', (value, context) =>
        hasAddress(value, context),
      ),
    national_id_type_code: Yup.string()
      .max(10)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test('national_id_type_required', 'ID Type is required when National ID is provided', (value, context) => {
        const hasNationalId = Boolean(context.parent.national_id)

        // If any other National ID fields are provided, this field is required
        if (hasNationalId && !value) {
          return false
        }
        return true
      }),
    national_id: Yup.string()
      .max(35)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test('national_id_required', 'National ID is required when ID Type is provided', (value, context) => {
        const hasIdType = Boolean(context.parent.national_id_type_code)

        // If any other National ID fields are provided, this field is required
        if (hasIdType && !value) {
          return false
        }
        return true
      }),
    national_id_issue_country: Yup.string()
      .max(2)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test('issue_country_not_required', 'Country Issuer must not be specified for entity accounts', (value) => {
        if (value) {
          return false
        }

        return true
      }),
    national_id_registration_authority: Yup.string()
      .max(10)
      .nullable()
      .test('required-identifiers', 'Customer ID, National ID, or Address is required', (value, context) =>
        validateRequiredAccountFields(context),
      )
      .test('registration_authority_conditional', (value, context) => {
        const idType = context.parent.national_id_type_code

        // Skip validation if ID Type is not provided
        if (!idType) {
          return true
        }

        // For LEIX type, registration authority must NOT be provided
        if (idType === 'LEIX') {
          if (value) {
            return context.createError({
              path: context.path,
              message: 'Registration Authority must not be specified for national ID type "LEIX"',
            })
          }
          return true
        }

        // For all other ID types, registration authority is required
        if (!value) {
          return context.createError({
            path: context.path,
            message: `Registration Authority is required for national ID type "${idType}"`,
          })
        }

        return true
      }),
  })

  // Functions
  const triggerSubmit = () => {
    formRef.current.submitForm()
  }

  const handleOnSubmit = (values) => {
    // Create a copy of values to modify
    const formValues = { ...values }

    // Check if any address fields are filled
    const hasAddressLine = Boolean(formValues.address_line)
    const hasStreetNo = Boolean(formValues.address_street_no)
    const hasStreetName = Boolean(formValues.address_street_name)
    const hasBuildingName = Boolean(formValues.address_building_name)

    // If any core address fields are provided, add address_type = 'GEOG'
    const hasAddress = hasAddressLine || (hasStreetName && (hasStreetNo || hasBuildingName))

    if (hasAddress) {
      formValues.address_type_code = 'GEOG'
    }

    const payload = {
      biz_id: userCredentials.Business_ID,
      ...formValues,
    }

    // removing empty fields on object
    Object.keys(payload).forEach((k) => payload[k] === '' && delete payload[k])

    if (isCreateAccount) {
      createAccountEntity(payload)
    } else if (!isCreateAccount) {
      updateAccountEntity({ account_id: toBeUpdatedAccount.id, values: payload })
    }
  }

  const handleLoadingChange = () => {
    if (isAccountEntityCreateLoading || isAccountEntityUpdateLoading) {
      setShowHeaderLoader(true)
    }
    if (!isAccountEntityCreateLoading && !isAccountEntityUpdateLoading) {
      setShowHeaderLoader(false)
      if (initialData) {
        if (isCreateAccount) {
          if (isAccountEntityCreateSuccess) {
            setCreatedAccount(accountEntityCreateData)
            setIsEntityAccountDrawerOpen(false)
            setIsPageTableUpdated(true)
            showAlert({ type: 'success', message: 'Successfully created record' })
          } else if (!isAccountEntityCreateSuccess) {
            if (typeof accountEntityCreateError?.response?.data?.data === 'string') {
              showAlert({ type: 'error', message: accountEntityCreateError?.response?.data?.data })
              return
            }
            showAlert({ type: 'error', message: 'An error occurred in creating record' })
          }
        } else if (!isCreateAccount) {
          if (isAccountEntityUpdateSuccess) {
            setCreatedAccount(accountEntityUpdateData)
            setToBeUpdatedAccount(null)
            setIsEntityAccountDrawerOpen(false)
            setIsRecordUpdated(true)
            showAlert({ type: 'success', message: 'Successfully updated record' })
          } else if (!isAccountEntityUpdateSuccess) {
            if (typeof accountEntityUpdateError?.response?.data?.data === 'string') {
              showAlert({ type: 'error', message: accountEntityUpdateError?.response?.data?.data })
              return
            }
            showAlert({ type: 'error', message: 'An error occurred in updating record' })
          }
        }
      }
    }
  }

  function hasRequiredAddressFields(context) {
    const parent = context?.parent

    /*
      If the form already has required account fields
      (date and place of birth, customer id, or national id),
      skip required address fields validation
    */
    const hasRequiredAccountFields = validateRequiredAccountFields(context)
    if (hasRequiredAccountFields) {
      return true
    }

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasAddress = hasAddressLine || (hasStreetName && (hasStreetNo || hasBuildingName))

    return hasAddress
  }

  function hasAddress(value, context) {
    const parent = context?.parent

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)

    // If no address info is provided, value is optional
    if (!hasAddressLine && !hasStreetNo && !hasStreetName && !hasBuildingName) {
      return true
    }

    // If we have validated address information, value is required
    return Boolean(value)
  }

  /*
    Validates if at least one of:
    - National ID
    - Address
    - Customer ID
    is provided.

    If an attempt to provide one of these identifiers is made,
    even if the identifier is incomplete
    (e.g. specifying incomplete address, national ID, or date and place of birth),
    the validation will pass.
  */
  function validateRequiredAccountFields(context) {
    const parent = context?.parent

    // Check if any national ID fields are provided
    // The validation here is more relaxed, since we also require the other field if one is provided
    const hasNationalIdType = Boolean(parent?.national_id_type_code)
    const hasNationalID = Boolean(parent?.national_id)
    const hasNationalIDIssueCountry = Boolean(parent?.national_id_issue_country)
    const hasNationalIDRegistrationAuthority = Boolean(parent?.national_id_registration_authority)

    const hasNationalIDInfo =
      hasNationalID || hasNationalIdType || hasNationalIDIssueCountry || hasNationalIDRegistrationAuthority

    // Check if customer ID is provided
    const hasCustomerID = Boolean(parent?.customer_id)

    // Check if any required address is provided
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasBuildingName = Boolean(parent?.address_building_name)

    const hasAddress = hasAddressLine || hasStreetName || hasStreetNo || hasBuildingName

    // Return true if at least one identifier method is provided
    return hasNationalIDInfo || hasAddress || hasCustomerID
  }

  // UseEffects
  useEffect(() => {
    if (!toBeUpdatedAccount) {
      setIsCreateAccount(true)
      // TODO REMOVE HARD CODED VALUE
      setInitialData({ ...initialValues, address_type_code: 'GEOG' })
      setInitialData(initialValues)
    } else {
      setIsCreateAccount(false)
      setInitialData({ ...toBeUpdatedAccount, address_type_code: 'GEOG' })
    }
  }, [])
  useEffect(() => handleLoadingChange(), [isAccountEntityCreateLoading, isAccountEntityUpdateLoading])
  return (
    <FormWrapper>
      {initialData && (
        <Form
          formRef={formRef}
          initialValues={initialData}
          validationSchema={validationSchema}
          onSubmit={handleOnSubmit}
        >
          <FormHeader>
            <FormTitle>{isCreateAccount ? 'Create Entity Account' : 'Edit Entity Account'}</FormTitle>
            {(accountEntityCreateError || accountEntityUpdateData) && (
              <FormError>
                {accountEntityCreateError?.response?.data?.message || accountEntityUpdateData?.response?.data?.message}
              </FormError>
            )}
          </FormHeader>

          <FormContainer>
            <FormInputGroupItem>
              <FormTextField label="Reference ID e.g. ID0001" name="reference_id" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Customer ID" name="customer_id" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormSelect label="Is Customer" name="is_customer" options={BOOLEAN_OPTIONS} />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Email" type="email" name="email" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Legal Name" name="name_legal" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormTextField label="Business Name" name="name_business" />
            </FormInputGroupItem>
            <FormInputGroupItem>
              <FormSearchSelect
                label="Registration Country"
                name="registration_country_code"
                options={COUNTRIES_LIST}
              />
            </FormInputGroupItem>

            <AccordionTableItem
              style={{ marginTop: '20px' }}
              setIsExpanded={setIsStructuredExpanded}
              expandPanel={isStructuredExpanded}
              button={
                <TableButtonWrapper>
                  <TableRowWrapper isExpanded={isStructuredExpanded}>Structured Address (preferred)</TableRowWrapper>
                </TableButtonWrapper>
              }
              panel={
                <TablePanelWrapper>
                  <FormInputGroupItem>
                    <FormTextField label="Street Number*" name="address_street_no" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Street Name*" name="address_street_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Building Name" name="address_building_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Floor" name="address_floor" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Room" name="address_room" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Department" name="address_department" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Postbox" name="address_postbox" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="City Location" name="address_city_location" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="City*" name="address_city" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Region" name="address_region" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="District" name="address_district" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="Zipcode" name="address_postcode" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect label="Country*" name="address_country_code" options={COUNTRIES_LIST} />
                  </FormInputGroupItem>
                </TablePanelWrapper>
              }
            />
            <AccordionTableItem
              style={{ marginTop: '20px' }}
              setIsExpanded={(val) => {
                setIsFreeFormExpanded(val)
              }}
              expandPanel={isFreeFormExpanded}
              button={
                <TableButtonWrapper>
                  <TableRowWrapper isExpanded={isFreeFormExpanded}>Free Form Address</TableRowWrapper>
                </TableButtonWrapper>
              }
              panel={
                <TablePanelWrapper>
                  <FormInputGroupItem>
                    <FormTextArea label="Address Line" name="address_line" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="City" name="address_city" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect label="Country" name="address_country_code" options={COUNTRIES_LIST} />
                  </FormInputGroupItem>
                </TablePanelWrapper>
              }
            />

            <AccordionTableItem
              style={{ marginTop: '20px' }}
              setIsExpanded={(val) => {
                setIsNationalIDExpanded(val)
              }}
              expandPanel={isNationalIDExpanded}
              button={
                <TableButtonWrapper>
                  <TableRowWrapper isExpanded={isNationalIDExpanded}>National ID</TableRowWrapper>
                </TableButtonWrapper>
              }
              panel={
                <TablePanelWrapper>
                  <FormInputGroupItem>
                    <FormSelect
                      label="National ID Type"
                      name="national_id_type_code"
                      options={NATIONAL_ID_TYPE_ENTITY_OPTIONS}
                    />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField label="National ID" name="national_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField
                      label="National ID Registration Authority"
                      name="national_id_registration_authority"
                    />
                  </FormInputGroupItem>
                </TablePanelWrapper>
              }
            />
          </FormContainer>

          <FormFooter>
            <Button type="button" onClick={triggerSubmit}>
              {isCreateAccount ? 'Create Entity Account' : 'Save Entity Account'}
            </Button>
          </FormFooter>
        </Form>
      )}
    </FormWrapper>
  )
}

// Default Props
EntityAccountForm.defaultProps = {
  formRef: {},
  form: {},
  actions: {},
}

// Proptypes Validation
EntityAccountForm.propTypes = {
  formRef: PropTypes.shape({ root: PropTypes.string }),
  form: PropTypes.shape({
    COUNTRIES_LIST: PropTypes.instanceOf(Array),
    toBeUpdatedAccount: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  actions: PropTypes.shape({
    setCountryListOptions: PropTypes.func,
    setIsRecordUpdated: PropTypes.func,
    setIsEntityAccountDrawerOpen: PropTypes.func,
    setToBeUpdatedAccount: PropTypes.func,
    setIsPageTableUpdated: PropTypes.func,
    showAlert: PropTypes.func,
    setShowHeaderLoader: PropTypes.func,
    setCreatedAccount: PropTypes.func,
  }),
}

export default connect(mapStateToProps, mapDispatchToProps)(EntityAccountForm)
